import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { LogUtils } from "abweb/utils/log/LogUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import "@abinitio/kendo.all";

// The big 5 that we ship languages for
import "@abinitio/kendo.all/js/cultures/kendo.culture.en.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.es.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.fr.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.ja.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.zh-CN.min";
// Other common locales for customers
import "@abinitio/kendo.all/js/cultures/kendo.culture.de.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.en-GB.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.pl.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.sv.min";
import "@abinitio/kendo.all/js/cultures/kendo.culture.tr.min";
// This one is not provided by kendo, so it is loaded from assets
// Only mhub ever supported this, loaded in AbEmmsServicesInitializer
// import "abemms/assets/custom-kendo-culture.en-FR.min.js";

const LOG = LogUtils.getLogger(__filename);
export class KendoLocaleUtils {
  /**
   * Sets kendo culture according to datetime locale.  If the datetime locale is not supported by loaded Kendo culture,
   * then fallback to using the locale aligned with the user's language choice.
   */
  static onLocaleChange(localeState) {
    // Note: kendo datetime formatting is primarily only done by legacy (extension) set usage of DateUtils these days,
    // and the kendo localized patterns delegate to DateTimeUtils to render the localized formats.  As such, the kendo
    // datetime formatting is primarily used for explicit hardcoded formats that are locale-independent
    // (e.g., "yyyy-MM-dd") so it doesn't really matter as much what the kendo culture (locale) is set to these days.
    // However, still try to get best locale match in case there are some obscure localized formats that are still be
    // handled by kendo.
    // The other place where it matters is if the kendo format strings that have the
    // date/datetime 'long' formats that spell out the month or day name (December, Monday),
    // as they will be in the language of the datetime locale.
    const localeStateKey = KendoLocaleUtils.calcLocaleStateKey(localeState);
    if (KendoLocaleUtils.lastLocaleStateKey !== localeStateKey) {
      const {
        datetimeFormatLocale,
        languageLocale
      } = localeState;
      const successfulNegotiation = KendoLocaleUtils.negotiateKendoCulture(datetimeFormatLocale, "datetime");
      if (!successfulNegotiation && datetimeFormatLocale !== languageLocale) {
        // The kendo cultures are loaded above for each of the supported languages: en, es, fr, ja, zh-CN
        KendoLocaleUtils.negotiateKendoCulture(languageLocale, "language");
      }
      KendoLocaleUtils.lastLocaleStateKey = localeStateKey;
    }
    LOG.info("Current kendo culture (aka datetime locale): " + kendo.culture().name);
    return Promise.resolve();
  }
  static calcLocaleStateKey(localeState) {
    // Does not depend on numberFormatLocale.  Renegiated locale if either of the below changes
    return localeState.datetimeFormatLocale + "::" + localeState.languageLocale;
  }
  static negotiateKendoCulture(newLocale, localeDescription) {
    // kendo.culture tries country-region then backs off to country of locale (en-US, then en)
    // See http://docs.telerik.com/kendo-ui/api/javascript/kendo#methods-culture
    kendo.culture(newLocale);
    const success = StringUtils.startsWith(newLocale, kendo.culture().name);
    if (!success) {
      LOG.warn(`Was not able to load the kendo culture corresponding to ${localeDescription} locale: '${newLocale}'`);
    }
    return success;
  }
}
// Supported subset of token characters, including supported whitespace, that matches exacly what is documented in:
// https://docs-stage.abinitio.com/r/416/Metadata_Hub/Metadata_Hub_Reporters_Guide/PART_TWO_Creating_reports/Inserting_and_changing_columns/Changing_column_grouping_name_appearance_and_formatting_of_special_values/Formatting_columns_containing_datetime_values
_defineProperty(KendoLocaleUtils, "SupportedKendoTokenSubset", /^(?:[ ,:.\\/\-]|[dfhHmMsy]|tt|yy|zzz)*$/);
_defineProperty(KendoLocaleUtils, "lastLocaleStateKey", void 0);
//# sourceMappingURL=KendoLocaleUtils.js.map
